<template>
  <div class="producao-kpi-page">
    <PageHeader :title="t('Dashboard de Produção')" />

    <!-- Loading State -->
    <template v-if="isLoading && !cards">
      <div class="loading-section">
        <DashboardSkeletonBlock height="350px" radius="16px" testid="producao-kpi-skeleton" />
      </div>
      <div class="kpi-grid kpi-grid--5 loading-section">
        <DashboardSkeletonBlock height="120px" radius="16px" v-for="n in 5" :key="'card-'+n" />
      </div>
    </template>

    <!-- Error State -->
    <div v-else-if="error && !cards" class="empty-state">
      <i class="pi pi-exclamation-triangle empty-icon"></i>
      <p>{{ t('Erro ao carregar dados de produção') }}</p>
      <Button :label="t('Tentar novamente')" @click="fetchAll" data-testid="producao-kpi-retry" />
    </div>

    <!-- Empty State -->
    <div v-else-if="cards && isEmptyData() && !isLoading" class="empty-state">
      <i class="pi pi-info-circle empty-icon"></i>
      <p>{{ t('Nenhum dado de produção encontrado para o período selecionado') }}</p>
    </div>

    <!-- Dashboard Content -->
    <template v-else-if="cards">
      <!-- Faixa Superior: Gráfico de Categorias -->
      <div class="chart-box">
        <charts
          type="bar"
          :series="servicosMensalSeries"
          :options="servicosMensalOptions"
          :is-loading="isLoading"
          height="350px"
        />
      </div>

      <!-- Faixa Inferior: Cards -->
      <div class="kpi-grid kpi-grid--5 cards-section">
        <StatsCard
          :value="formatDias(cards.prazo_medio_entrega)"
          :label="t('Média de Prazo de Entrega de OS')"
          icon="pi pi-calendar-clock"
          variant="info"
        />
        <StatsCard
          :value="formatDecimal(cards.media_entrada_itens)"
          :label="t('Média de Entrada de Itens')"
          icon="pi pi-arrow-right"
          variant="primary"
        />
        <StatsCard
          :value="formatDecimal(cards.media_producao_diaria)"
          :label="t('Média de Produção Diária')"
          icon="pi pi-cog"
          variant="success"
        />
        <StatsCard
          :value="formatDecimal(cards.meta_producao_diaria)"
          :label="t('META de Produção Diária')"
          icon="pi pi-flag"
          variant="warning"
        />
        <StatsCard
          :value="cards.itens_atrasados"
          :label="t('Itens ATRASADOS')"
          icon="pi pi-exclamation-triangle"
          :variant="cards.itens_atrasados > 0 ? 'danger' : 'success'"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDashboardProducaoKpi } from '../../composables/dashboard/useDashboardProducaoKpi';
import { useChartFormat } from '../../composables/useChartFormat';
import PageHeader from '../../ui/PageHeader.vue';
import DashboardSkeletonBlock from '../dashboard/widgets/DashboardSkeletonBlock.vue';
import StatsCard from '../../ui/StatsCard.vue';

const { t, n } = useI18n();
const { currencySymbol, monthLabel } = useChartFormat();
const {
  cards,
  servicosMensal,
  isLoading,
  error,
  fetchAll,
} = useDashboardProducaoKpi();

// Empty state: true when ALL cards values are zero (no naturally-zero metric drives this).
function isEmptyData(): boolean {
  const c = cards.value;
  if (!c) return false;
  return (
    c.prazo_medio_entrega === 0 &&
    c.media_entrada_itens === 0 &&
    c.media_producao_diaria === 0 &&
    c.meta_producao_diaria === 0 &&
    c.itens_atrasados === 0
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function formatDecimal(valor100: number): string {
  return n(valor100 / 100, 'decimal');
}

function formatDias(valor100: number): string {
  const dias = valor100 / 100;
  return `${n(dias, 'decimal')} ${t('dias')}`;
}

// ─── Chart: Serviços Mensais (Barras Empilhadas) ─────────────

const servicosMensalSeries = computed(() => {
  if (!servicosMensal.value?.length) return [];

  // Collect all unique categories across all months.
  const allCategories = new Set<string>();
  const months = servicosMensal.value;

  for (const mes of months) {
    const cats = mes.categorias ?? [];
    for (const cat of cats) {
      allCategories.add(cat.categoria);
    }
  }

  const categoryList = Array.from(allCategories).sort();

  // Build one series per category.
  return categoryList.map((categoria) => ({
    name: categoria,
    data: months.map((mes) => {
      const cats = mes.categorias ?? [];
      const match = cats.find((c) => c.categoria === categoria);
      return match ? Math.round((match.valor ?? 0) / 100) : 0;
    }),
  }));
});

const servicosMensalOptions = computed(() => ({
  chart: { stacked: true, toolbar: { show: true } },
  xaxis: {
    categories: servicosMensal.value?.map((m) => monthLabel(m.mes)) ?? [],
    type: 'category' as const,
  },
  yaxis: { title: { text: t('Valor ({moeda})', { moeda: currencySymbol() }) } },
  dataLabels: { enabled: false },
  tooltip: {
    shared: true,
    intersect: false,
    y: {
      formatter: (val: number) => n(val, 'currency'),
    },
  },
}));
</script>

<style scoped>
.producao-kpi-page {
  padding: 20px;
}

/* Grid */
.kpi-grid {
  display: grid;
  gap: 1rem;
}
.kpi-grid--5 { grid-template-columns: repeat(5, 1fr); }

@media (max-width: 1100px) {
  .kpi-grid--5 { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 900px) {
  .kpi-grid--5 { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 500px) {
  .kpi-grid--5 { grid-template-columns: 1fr; }
}

.chart-box {
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 12px;
  padding: 1rem;
}

/* Empty & Error States */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--p-text-color-secondary);
  text-align: center;
  gap: 1rem;
}

.empty-icon {
  font-size: 3rem;
  opacity: 0.5;
}

/* Spacer classes replacing inline margin-top */
.loading-section { margin-top: 1rem; }
.cards-section { margin-top: 1rem; }
</style>
